import type { User } from "@/types/user";

import { createId, pickMultiple } from "./base";

import {
  TEAM_ASSIGNMENT_STRATEGY,
  type Team,
  type TeamAssignmentStrategy,
  type TeamMember,
} from "@/types/team";

/** Map a workspace user onto the slimmer TeamMember shape the API returns. */
export function toTeamMember(user: User): TeamMember {
  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone,
    imageUrl: user.imageUrl,
    externalId: user.externalId,
    title: user.title ?? null,
    timezone: user.timezone ?? null,
    language: user.language ?? null,
  };
}

interface TeamSeed {
  name: string;
  description: string;
  color: string;
  assignmentStrategy: TeamAssignmentStrategy;
}

const TEAM_SEEDS: TeamSeed[] = [
  {
    name: "Front Desk",
    description: "Handles first-touch enquiries and appointment booking.",
    color: "#14b8a6",
    assignmentStrategy: TEAM_ASSIGNMENT_STRATEGY.ROUND_ROBIN,
  },
  {
    name: "Sales",
    description: "Converts qualified leads and manages treatment packages.",
    color: "#6366f1",
    assignmentStrategy: TEAM_ASSIGNMENT_STRATEGY.LEAST_ASSIGNED,
  },
  {
    name: "Patient Support",
    description: "Aftercare, follow-ups and existing patient questions.",
    color: "#ec4899",
    assignmentStrategy: TEAM_ASSIGNMENT_STRATEGY.ONLINE,
  },
];

/**
 * Generate workspace teams, each staffed with a random subset of users.
 */
export function generateTeams(users: User[]): Team[] {
  if (users.length === 0) return [];

  return TEAM_SEEDS.map((seed) => {
    const members = pickMultiple(users, 2, 4).map(toTeamMember);

    return {
      id: createId(),
      name: seed.name,
      description: seed.description,
      color: seed.color,
      assignmentStrategy: seed.assignmentStrategy,
      members,
      memberCount: members.length,
    };
  });
}
